// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * `useParsedLatestProgress` — the dashboard's single sanctioned consumer of
 * the latest-progress read. Wraps `useCycleLatestProgress` with the
 * `_progressPayload` parse guard so every panel that needs the accumulating
 * tier reads a ready-parsed `CycleProgress` instead of repeating the
 * `isSuccess && data != null ? parse(...) : null` fold.
 *
 * A present row parses to a `CycleProgress`; an absent one (404 before the
 * first tick, or a malformed payload) is `null` — never a throw into the
 * render (the tolerance lives in `parseCycleProgressPayload`). The raw
 * generated wire-type name stays sealed behind `_progressPayload.ts`; this
 * hook exposes only the `CycleProgress` seam alias, so views never name the
 * generated type directly (`progressSeamBoundary.test.ts`).
 */

import { useCycleLatestProgress } from "@/queries/queries.eventJournal"
import { parseCycleProgressPayload, type CycleProgress } from "./_progressPayload"

export interface ParsedLatestProgress {
  progress: CycleProgress | null
  isLoading: boolean
}

export const useParsedLatestProgress = (
  cycleId: string | undefined,
): ParsedLatestProgress => {
  const query = useCycleLatestProgress(cycleId)
  const progress =
    query.isSuccess && query.data != null
      ? parseCycleProgressPayload(query.data.payload_json)
      : null
  return { progress, isLoading: query.isLoading }
}
